import {
  Button,
  darken,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import withStyles from '@mui/styles/withStyles';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilStateLoadable, useRecoilValueLoadable } from 'recoil';

import { useBulkChangeProductsMutation } from '../../../api/apigQuery';
import { IUserAtom, Role, user } from '../../../recoil/atoms/user';

import { ModalWithStyles } from './ModalWithStyles';

import { permissionsSelector } from '../../../recoil/selectors/permissionsSelector';

const StyledTableCell = withStyles((theme) => ({
  head: {
    fontWeight: 600,
    padding: '8px',
  },
  body: {
    padding: '8px',
  },
}))(TableCell);
const useStyles = makeStyles((theme: Theme) => ({
  dealButton: {
    backgroundColor: 'rgb(98,93,236)',
    '&:hover': {
      backgroundColor: darken(
        'rgb(98,93,236)',
        theme.palette.mode === 'light' ? 0.2 : 0
      ),
    },
    '&:focus': {
      backgroundColor: darken(
        'rgb(98,93,236)',
        theme.palette.mode === 'light' ? 0.2 : 0
      ),
    },
  },
}));
const ProductChangesConfirmModal = ({
  show = false,
  handleClose,
  userProductChanges,
  onHandleSubmit,
  enqueueSnackbarWithStyles,
  currentUser,
  newAgent = false,
  productChangesLoading = false,
}) => {
  const classes = useStyles();
  const [bulkProductMutation] = useBulkChangeProductsMutation();
  const [userAtomLoadable, _setUserAtom] = useRecoilStateLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const [preflightAgentChanges, setPreflightAgentChanges] = useState([]);
  useEffect(() => {
    const preflightUpdateAgentChanges = async () => {
      setPreflightAgentChanges([]);
      try {
        const res: any = await bulkProductMutation({
          userSettings: {
            apikey: userAtomContents?.apikey,
            businessId: userAtomContents?.businessid,
            userId: userAtomContents?.userid,
            businessid: userAtomContents?.businessid,
            userid: userAtomContents?.userid,
          },
          preflight: 1,
          content: userProductChanges,
        });
        if (res?.data?.content?.length) {
          setPreflightAgentChanges(res?.data?.content);
        }
      } catch (err) {
        enqueueSnackbarWithStyles({
          message: `Error Getting curent products for user.`,
          alertProps: { severity: 'error' },
        });
        handleClose();
      }
    };
    userProductChanges?.length > 0 && preflightUpdateAgentChanges();
  }, [userProductChanges]);
  // TODO ONLY USE BULK ON MULTIPLE AGENTS
  // const updateUserProducts = async () => {
  //   try {
  //     const res: any = await bulkProductMutation({
  //       userSettings: {
  //         apikey: userAtomContents?.apikey,
  //         businessId: userAtomContents?.businessid,
  //         userId: userAtomContents?.userid,
  //         businessid: userAtomContents?.businessid,
  //         userid: userAtomContents?.userid,
  //       },
  //       content: userProductChanges,
  //       preflight: 0,
  //     });
  //     if (res?.data?.content?.length) {
  //       enqueueSnackbarWithStyles({
  //         message: 'Successfully Updated Agent Products',
  //         alertProps: { severity: 'success' },
  //       });
  //     }
  //   } catch (err) {
  //     enqueueSnackbarWithStyles({
  //       message: `Agent product changes not saved.`,
  //       alertProps: { severity: 'error' },
  //     });
  //   }
  // };
  const handleSubmit = (val: boolean) => {
    onHandleSubmit(val);
  };
  const userFirstName = useMemo(() => currentUser?.fname ?? '', [currentUser]);
  const userLastName = useMemo(() => currentUser?.lname ?? '', [currentUser]);

  const proratedPrice = useMemo(() => {
    let price = 0;
    preflightAgentChanges?.forEach((el, i) => {
      if (
        el.action !== 'cancel' &&
        el.action !== 'unchanged' &&
        el.action !== 'downgrade'
      ) {
        price = price + el?.proratedPrice ?? 0;
      }
    });
    return price;
  }, [preflightAgentChanges]);

  const modalTitle = useMemo(() => {
    let t = newAgent ? 'Add Agent' : 'Edit Agent';
    if (
      userFirstName &&
      userFirstName !== '' &&
      userLastName &&
      userLastName !== ''
    ) {
      t = t + ' ' + userFirstName + ' ' + userLastName;
    }
    return t;
  }, [userFirstName, userLastName, newAgent]);

  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { role } = permissionsLoadable.contents;

  return (
    <ModalWithStyles
      title={modalTitle}
      aria-labelledby="example-modal-sizes-title-md"
      show={show}
      onHide={handleClose}
      size="sm"
      deleteDialog={false}
      isLoading={productChangesLoading}
      footerComponent={
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignContent: 'center',
              width: '100%',
            }}
          >
            <div>
              {!newAgent && role === Role.SUPERADMIN && (
                <Button
                  color="primary"
                  variant="contained"
                  disabled={preflightAgentChanges?.length == 0}
                  className={`superStyle ${classes.dealButton}`}
                  disableElevation={true}
                  onClick={async () => {
                    // await updateUserProducts();
                    handleSubmit(true);
                  }}
                >
                  Save and Apply to More Agents
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
              <Button
                variant="contained"
                color="inherit"
                disableElevation={true}
                onClick={handleClose}
              >
                Go Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                disableElevation={true}
                disabled={productChangesLoading}
                onClick={async () => {
                  // !newAgent && (await updateUserProducts());
                  handleSubmit(false);
                }}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <div>
        {preflightAgentChanges?.length == 0 ? (
          // <div style={{ padding: '24px 42px' }}>
          <div>
            <Skeleton
              variant="rectangular"
              style={{ marginBottom: 5, marginTop: 20 }}
            />
            <Skeleton variant="rectangular" style={{ marginBottom: 5 }} />
            <Skeleton
              variant="rectangular"
              style={{ marginBottom: 15, width: '50%' }}
            />
            <Skeleton variant="rectangular" style={{ marginBottom: 5 }} />
            <Skeleton
              variant="rectangular"
              style={{ marginBottom: 15, width: '50%' }}
            />
            <Skeleton variant="rectangular" style={{ marginBottom: 5 }} />
            <Skeleton variant="rectangular" style={{ marginBottom: 5 }} />
            <Skeleton
              variant="rectangular"
              style={{ marginBottom: 15, width: '50%' }}
            />
            <Skeleton variant="rectangular" style={{ marginBottom: 5 }} />
          </div>
        ) : (
          // <div style={{ padding: '24px 42px' }}>
          <div style={{ padding: '12px' }}>
            <Paper elevation={0}>
              <div>
                <p>
                  You are about to {newAgent ? 'add' : 'update'} product
                  subscriptions for{' '}
                  {`${
                    userFirstName === '' && userLastName === ''
                      ? '1 Agent'
                      : 'agent ' + userFirstName + ' ' + userLastName
                  }`}
                  .
                </p>
                <p>
                  A prorated charge in the amount of{' '}
                  <b>${(proratedPrice ?? 0)?.toFixed(2)}</b> will be applied
                  instantly and your next invoice will be updated to reflect the
                  below product subscriptions:
                </p>
              </div>
              <TableContainer>
                <Table
                  aria-label="a dense table"
                  style={{ border: '1px solid rgba(224, 224, 224, 1)' }}
                >
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Product</StyledTableCell>
                      <StyledTableCell>Action</StyledTableCell>
                      <StyledTableCell align="left">
                        Monthly Price
                      </StyledTableCell>
                      <StyledTableCell align="left">Prorated</StyledTableCell>
                      <StyledTableCell>Bill Cycle</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preflightAgentChanges?.map(
                      (productChange, i) =>
                        (productChange?.action === 'add' ||
                          productChange?.action === 'downgrade' ||
                          productChange?.action === 'unchanged' ||
                          productChange?.action === 'upgrade' ||
                          productChange?.action === 'cancel') && (
                          <TableRow key={productChange?.newDisplayName + i}>
                            <StyledTableCell component="th" scope="row">
                              {productChange?.action === 'add' ||
                              productChange?.action === 'downgrade' ||
                              productChange?.action === 'upgrade' ||
                              productChange?.action === 'unchanged'
                                ? productChange?.newDisplayName?.toLowerCase() ===
                                  'ftc blocking'
                                  ? 'DNC Algorithim'
                                  : productChange?.newDisplayName?.toLowerCase() ===
                                    'local presence lite'
                                  ? 'Connection Boost'
                                  : productChange?.newDisplayName
                                : productChange?.oldDisplayName?.toLowerCase() ===
                                  'ftc blocking'
                                ? 'DNC Algorithim'
                                : productChange?.oldDisplayName?.toLowerCase() ===
                                  'local presence lite'
                                ? 'Connection Boost'
                                : productChange?.oldDisplayName}
                            </StyledTableCell>
                            <StyledTableCell>
                              {productChange?.action?.charAt(0).toUpperCase() +
                                productChange?.action?.slice(1)}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              {productChange?.action === 'cancel'
                                ? '-'
                                : productChange?.newCanHaveDiscount === 1 &&
                                  productChange?.discountPercent > 0
                                ? '$' +
                                  (
                                    productChange?.newPricePerUnit -
                                    productChange?.newPricePerUnit *
                                      (productChange?.discountPercent / 100)
                                  ).toFixed(2)
                                : '$' +
                                  (productChange?.newPricePerUnit ?? 0).toFixed(
                                    2
                                  )}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              {productChange?.action === 'cancel' ||
                              productChange?.action === 'downgrade' ||
                              productChange?.action === 'unchanged'
                                ? '-'
                                : '$' +
                                  productChange?.proratedPrice?.toFixed(2)}
                            </StyledTableCell>

                            <StyledTableCell>
                              {productChange?.productBillingCycle + ' month'}
                            </StyledTableCell>
                          </TableRow>
                        )
                    )}
                    <TableRow>
                      <StyledTableCell colSpan={3}>
                        <b>Total</b>
                      </StyledTableCell>
                      <StyledTableCell align="left">
                        <b>{`$${proratedPrice?.toFixed(2) ?? 0}`}</b>
                      </StyledTableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
            <div
              style={{
                padding: '12px 0',
                fontWeight: '600',
              }}
            >
              Are you sure you want to save and continue?
            </div>
          </div>
        )}
      </div>
    </ModalWithStyles>
  );
};
export default ProductChangesConfirmModal;
